<template>
  <VPopup
    v-model="visible"
    :class="['m-share-container', ...classNames]"
    type="popup-bottom"
    @close="handleClose"
  >
    <div
      v-loading="loading"
      class="tw-overflow-hidden tw-mx-auto"
    >
      <slot name="share-top" />

      <slot>
        <div
          v-if="shareTitle"
          class="m-share-title"
        >
          {{ shareTitle }}
        </div>
        <div
          v-if="hasShareInfo"
          class="m-share-info tw-mx-[30px]"
        >
          <p
            v-if="shareTopTips"
            class="m-share-tips"
          >
            {{ shareTopTips }}
          </p>
          <div
            v-if="shareImgs"
            ref="preview"
            class="m-thumbnail"
            @click="show"
          >
            <img :src="imgData" />
            <a class="m-preview">
              <InlineSvg
                width="36px"
                color="#fff"
                icon="~public/images/inline/zoom.svg"
              />
            </a>
          </div>

          <div
            v-if="hasHideAmountBtn"
            class="m-append"
            @click="toggleHideAmount"
          >
            <span class="m-share-checkbox">
              <keep-alive>
                <InlineSvg
                  v-if="hideAmount"
                  icon="~public/images/inline/selected-circle.svg"
                />
                <InlineSvg
                  v-else
                  icon="~public/images/inline/select-circle.svg"
                />
              </keep-alive>
            </span>
            <span>{{ hideText }}</span>
          </div>

          <p
            v-if="afterPreviewMessage"
            class="m-after-preview-message"
          >
            {{ afterPreviewMessage }}
          </p>
        </div>
      </slot>

      <slot name="share-bottom" />

      <div class="m-share-platforms">
        <p
          v-if="shareTips"
          class="m-tips tw-mx-[30px]"
        >
          {{ shareTips }}
        </p>
        <div
          class="plateforms-operate"
          :class="{
            'has-toplist': hasTopList
          }"
        >
          <!-- 用户自定义分享 -->
          <slot name="custom-share" />

          <!-- today's top list -->
          <a
            v-if="hasTopList"
            class="m-flex-item"
            @click="gotoToplist"
          >
            <div class="m-img-container">
              <img
                v-if="canTopListShare"
                src="~public/images/topList.png"
              />
              <img
                v-else
                src="~public/images/topList-disabled.png"
              />
            </div>
            <div>Today's<br />Top List</div>
          </a>

          <!-- facebook -->
          <a
            class="m-flex-item m-facebook"
            @click="shareToFacebook"
          >
            <div class="m-img-container">
              <img
                src="~public/images/facebook@2x.png"
                alt="facebook"
              />
            </div>
            <div>Facebook</div>
          </a>
          <!-- whatsapp -->
          <div
            class="m-flex-item m-whatsapp"
            data-action="share/whatsapp/share"
            @click="shareToWhatsapp"
          >
            <div class="m-img-container">
              <img
                src="~public/images/whatsapp@2x.png"
                alt="whatsapp"
              />
            </div>
            <div>WhatsApp</div>
          </div>
          <!-- Twitter -->
          <div
            class="m-flex-item m-twitter"
            @click="shareToTwitter"
          >
            <div class="m-img-container">
              <img
                src="~public/images/x@2x.png"
                alt="x.com"
              />
            </div>
            <div>X</div>
          </div>

          <!-- copy link -->
          <div
            v-if="copyLinkShow"
            ref="copyLink"
            class="m-flex-item m-copyLink"
            :data-clipboard-text="copyInfo"
          >
            <div class="m-img-container">
              <img
                src="~public/images/copy-link@2x.png"
                alt="copy"
              />
            </div>
            <div>Copy Link</div>
          </div>
          <!-- save -->
          <div
            v-if="shouldDownloadVisible"
            class="m-flex-item"
            @click="downloadToApp"
          >
            <InlineSvg icon="~public/images/inline/save.svg" />
            <div>Save</div>
          </div>
        </div>
      </div>
    </div>
  </VPopup>
</template>

<script>
/**
 * 分享组件，可分享到 facebook, whatsapp, twitter,copy link, save 到本地等
 */
import ClipboardJS from 'clipboard'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@ms/stores/app'
import { openDialog } from '@ms/ui/dialog'
import VPopup from 'fe-vui/popup'
import toast from 'fe-vui/toast'
import { prefixLink } from '@ms/config/app'
import { shareToAppPlatform } from 'wap/hybrid'
import download from 'wap/utils/download.js'
import { copiedMessage } from '@ms/config/message'

export default {
  name: 'ShareModule',
  components: {
    VPopup
  },
  model: {
    prop: 'outVisible',
    event: 'close'
  },
  props: {
    // 自定义 claass
    classNames: {
      type: Array,
      default: () => []
    },
    outVisible: {
      type: Boolean,
      default: false
    },
    // 分享渠道前缀, 会自动追求 fb/tw/wa/copy 和 wap 后缀
    channelPrefix: {
      type: String,
      default: ''
    },
    // 通用分享链接
    shareUrl: {
      type: String,
      default: location.href
    },
    // 分享文案
    shareText: {
      type: String,
      default: ''
    },
    // 开发者指定的话题标签，将添加至分享的内容中。用户可决定是否在分享对话框中移除这种话题标签。话题标签应包含 # 符号，例如 #facebook。
    hashtag: {
      type: String,
      default: ''
    },
    hasShareInfo: {
      type: Boolean,
      default: true
    },
    shareTitle: {
      type: String,
      default: ''
    },
    shareTopTips: {
      type: String,
      default: ''
    },
    shareImgs: {
      type: Array,
      default: null
    },
    // 是否显示隐藏金额按钮
    hasHideAmountBtn: {
      type: Boolean,
      default: true
    },
    // 默认是否隐藏金额
    defaultHideAmount: {
      type: Boolean,
      default: false
    },
    // 外层控制 hideAmount 是否选中
    hideAmountChecked: {
      type: Boolean,
      default: false
    },
    // 隐藏按钮的文案
    hideText: {
      type: String,
      default: 'Share with hidden amount'
    },
    downloadShow: {
      type: Boolean,
      default: false
    },
    copyLinkShow: {
      type: Boolean,
      default: true
    },
    afterPreviewMessage: {
      type: String,
      default: ''
    },
    shareTips: {
      type: String,
      default: ''
    },
    // 分享前回调
    callbackBeforeShare: {
      type: Function,
      default: null
    },
    hasTopList: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: String,
      default: ''
    },
    canTopListShare: {
      type: Boolean,
      default: false
    },
    topListTip: {
      type: String,
      default: ''
    }
  },
  setup() {
    const { isAppWebview } = storeToRefs(useAppStore())

    return {
      isAppWebview
    }
  },
  data() {
    return {
      hideAmount: this.defaultHideAmount,
      visible: false,
      loading: false
    }
  },
  computed: {
    imgData() {
      if (!this.shareImgs) return
      return !this.hideAmount ? this.shareImgs[0] : this.shareImgs[1]
    },
    whatsappShareText() {
      let shareUrl = this.appendChannel(this.shareUrl, 'wa')
      shareUrl = this.getHideAmountShareUrl(shareUrl)
      return encodeURIComponent(`${this.shareText} ${shareUrl}`)
    },
    copyInfo() {
      return this.appendChannel(this.shareUrl, 'copy')
    },
    shouldDownloadVisible() {
      // 有图
      return this.imgData && this.downloadShow
    },
    isBase64() {
      return /^data:.*;base64/.test(this.imgData)
    }
  },
  watch: {
    outVisible(val) {
      this.visible = val
    },
    visible(val) {
      if (val) {
        // 调起分享组件埋点
        window.mstrace('Share_Popup', 'Share')
      }
    },
    hideAmountChecked(val) {
      this.hideAmount = val
    }
  },
  created() {
    window.addEventListener(
      'hashchange',
      () => {
        if (!location.hash && this.$refs.preview) {
          const viewer = this.$refs.preview.$viewer
          viewer && viewer.hide()
        }
      },
      false
    )
  },
  mounted() {
    if (this.$refs.copyLink) {
      this.clipboard1 = new ClipboardJS(this.$refs.copyLink)
      this.clipboard1.on('success', (e) => {
        toast(copiedMessage)
        e.clearSelection()
        window.mstrace('share_copy')
      })
    }
  },
  beforeDestroy() {
    if (this.clipboard1) {
      this.clipboard1.destroy()
    }
  },
  methods: {
    getHideAmountShareUrl(shareUrl) {
      if (this.hideAmount) {
        shareUrl = /\?/.test(shareUrl) ? `${shareUrl}&h=1` : `${shareUrl}?h=1`
      }
      return shareUrl
    },
    toggleHideAmount() {
      this.hideAmount = !this.hideAmount
      this.$emit('hide-amount', this.hideAmount)
      window.mstrace('share_hideamount')
    },
    show() {
      import(/* webpackChunkName: 'viewer' */ '@ms/components/viewer/imageBrowser.js').then(({ default: imageBrowser }) => {
        const viewer = imageBrowser(this.$refs.preview)
        viewer.show()
        window.location.hash = 'preview'
      })
    },
    handleClose() {
      this.$emit('close', false, this.loading)
    },
    // 添加分享渠道后缀，type 如 fb/tw/wa/copy
    appendChannel(shareUrl, type) {
      // shareUrl 自带渠道则直接返回
      if (/from=/i.test(shareUrl)) return shareUrl
      const { channelPrefix } = this
      if (channelPrefix) {
        let channel = `from=${channelPrefix}`
        if (type) {
          channel += `_${type}_wap`
        }
        shareUrl = /\?/.test(shareUrl) ? `${shareUrl}&${channel}` : `${shareUrl}?${channel}`
      }
      return shareUrl
    },
    getShareLink(type) {
      // type:fb/tw/wa/copy
      const shareUrl = this.appendChannel(this.shareUrl, type)
      const shareLink = this.getHideAmountShareUrl(shareUrl)
      return shareLink
    },
    callAppSharePlatform(channel) {
      // channel:whatsapp,facebook,twitter,save,copy_link,more
      let type
      switch (channel) {
        case 'whatsapp':
          type = 'wa'
          break
        case 'facebook':
          type = 'fb'
          break
        case 'twitter':
          type = 'tw'
          break
        case 'copy_link':
          type = 'copy'
          break
        case 'save':
          type = 'save'
          break
        default:
          type = ''
      }
      return shareToAppPlatform({
        url: this.getShareLink(type),
        content: encodeURIComponent(this.shareText),
        image: this.isBase64 ? this.imgData : '',
        imageUrl: !this.isBase64 ? this.imgData : '',
        channel
      })
    },
    gotoToplist() {
      window.mstrace('share_toplist')
      if (this.canTopListShare) {
        const { orderId } = this
        location.href = prefixLink(`/discussion/comment/publish?orderId=${orderId}`)
      } else {
        openDialog({
          title: 'Sorry, this ticket cannot be shared.',
          message: this.topListTip,
          cancelButtonText: 'Cancel',
          confirmButtonText: "Check Today's List",
          onConfirm: () => {
            location.href = prefixLink('/discussion')
          }
        })
      }
    },
    async shareToFacebook() {
      window.mstrace('share_facebook')
      await this.beforeShareHook()
      this.$emit('social-share')
      if (this.isAppWebview) {
        // 仅1.4.6及以上版本支持在app内唤起facebook应用分享
        this.callAppSharePlatform('facebook')
      } else {
        const redirectUri = this.isAppWebview ? location.href : `${location.origin}${prefixLink()}`
        const url = `https://www.facebook.com/dialog/share?app_id=${process.env.VUE_APP_FACEBOOK_APP_ID}&display=popup&href=${encodeURIComponent(this.getShareLink('fb'))}&redirect_uri=${redirectUri}`
        window.open(url)
      }
    },
    async shareToWhatsapp() {
      window.mstrace('share_whatsapp')
      await this.beforeShareHook()
      this.$emit('social-share')
      if (this.isAppWebview) {
        // 仅1.4.6及以上版本支持在app内唤起whatsapp应用分享
        this.callAppSharePlatform('whatsapp')
      } else {
        // wap whatsapp 通过 href 分享
        location.href = `whatsapp://send?text=${this.whatsappShareText}`
        const loc = location.href
        setTimeout(() => {
          if (location.href === loc && !document.hidden) {
            this.handleClose()
            toast('Please install Whatsapp First')
          } else {
            this.$emit('success')
          }
        }, 2000)
      }
    },
    async shareToTwitter() {
      window.mstrace('share_twitter')
      await this.beforeShareHook()
      this.$emit('social-share')
      if (this.isAppWebview) {
        // 仅1.4.6及以上版本支持在app内唤起twitter应用分享
        this.callAppSharePlatform('twitter')
      } else {
        const shareUrl = this.appendChannel(this.shareUrl, 'tw')
        const shareLink = this.getHideAmountShareUrl(shareUrl)
        const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(this.shareText)}`
        window.open(url)
      }
    },
    // 分享前回调，可能有图片上传需求
    async beforeShareHook() {
      if (!this.callbackBeforeShare) return

      this.loading = true
      try {
        const index = !this.hideAmount ? 0 : 1
        await this.callbackBeforeShare(index)
        this.loading = false
      } catch (error) {
        console.error(error)
        this.loading = false
        return Promise.resolve()
      }
    },
    downloadToApp() {
      if (this.isAppWebview) {
        // 仅1.4.6及以上版本支持调用app内方法保存图片
        this.callAppSharePlatform('save')
      } else {
        download(this.imgData, 'share_download.png')
      }
      window.mstrace('share_download')
    }
  }
}
</script>

<style lang="less">
@import url('~wap/common/style/variable.less');

.popup-cotainer.m-share-container {
  z-index: 1499;

  .popup-wrap {
    border-radius: 16px 16px 0 0;
  }

  .m-share-platforms {
    .m-tips {
      margin: 10 / @rem 12 / @rem;
      color: #666;
      font-size: 10 / @rem;
      line-height: 14 / @rem;
    }
  }

  .plateforms-operate {
    display: flex;
    width: 100%;
    justify-content: center;
    padding: 10 / @rem 0;

    &.has-toplist {
      justify-content: flex-start;
      -webkit-overflow-scrolling: touch;
      overflow-x: auto;

      .m-flex-item {
        &:first-child {
          margin-left: 30px;
        }
      }

      @media screen and (min-width: 420px) {
        justify-content: center;

        .m-flex-item {
          &:first-child {
            margin: 0;
          }
        }
      }
    }

    .m-flex-item {
      display: block;
      width: 60/360 * 100%;
      max-width: 70px;
      flex: none;

      .inline-svg {
        width: 36 / @rem;
        margin-bottom: 12 / @rem;
      }

      .m-img-container {
        width: 36 / @rem;
        height: 48 / @rem;
        margin: 0 auto;
      }

      img {
        width: 100%;
        vertical-align: top;
      }

      div {
        color: #333;
        font-size: 10 / @rem;
      }
    }
  }
}

.m-share-title {
  padding: 20px 0 15px;
  color: #333;
  font-size: 18px;
  line-height: 22px;
}

.m-share-info {
  padding: 44 / @rem 0 9 / @rem;

  .m-share-tips {
    color: #333;
    font-size: 14 / @rem;
    letter-spacing: 0;
    line-height: 18 / @rem;
  }

  .m-thumbnail {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 150 / @rem;
    margin: 20 / @rem auto 10 / @rem;

    img {
      width: 100%;
      vertical-align: top;
    }

    &::after {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      display: block;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 50%);
      content: '';
    }

    .m-preview {
      position: absolute;
      z-index: 2;
      top: 50%;
      left: 50%;
      display: block;
      width: 36 / @rem;
      height: 36 / @rem;
      margin: -18 / @rem 0 0 -18 / @rem;
      line-height: 36 / @rem;
      text-align: center;
    }
  }

  .m-append {
    color: #333;
    cursor: pointer;
    font-size: 12 / @rem;
    letter-spacing: 0;
    line-height: 16 / @rem;

    .m-share-checkbox {
      display: inline-block;
      margin-right: 5 / @rem;
      vertical-align: top;

      .inline-svg {
        width: 14px;
        margin-top: -2px;
      }
    }
  }

  .m-after-preview-message {
    color: #333;
    font-size: 12 / @rem;
    line-height: 16 / @rem;
    text-align: center;
  }
}

.viewer-container {
  z-index: 10000 !important;
}

.viewer-button {
  background: rgba(116, 116, 116, 50%) !important;
}
</style>
